import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Settings,
  Menu,
  X,
  PlusCircle,
  Home,
  LogOut,
  MessageSquare,
  BookOpen,
  FileSearch,
  History,
  ChevronDown,
  ChevronUp,
  ScrollText
} from 'lucide-react';
import { useThemeStore } from '../store/themeStore';
import { useStore } from '../store/useStore';
import Logo from './Logo';
import AppTitle from './AppTitle';
import SidebarLink from './sidebar/SidebarLink';
import ThemeToggle from './sidebar/ThemeToggle';

export default function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed] = useState(false); // New state for collapsed sidebar
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode, toggleDarkMode } = useThemeStore();
  const { logoutUser } = useStore();

  const [isParecerOpen, setIsParecerOpen] = useState(true);

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-20 p-2 rounded-lg bg-white dark:bg-navy-800 shadow-md border border-navy-200 dark:border-navy-700"
      >
        <Menu className="h-6 w-6 text-navy-600 dark:text-navy-300" />
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-10 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div
        className={`fixed top-0 left-0 z-30 h-full transform bg-white dark:bg-navy-900 border-r border-navy-200 dark:border-navy-700 shadow-lg transition-all duration-300 ease-in-out lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'
          } ${isCollapsed ? 'w-20' : 'w-64'}`} // Conditional width based on isCollapsed
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between p-4">
            <Link to="/" className="flex items-center space-x-2">
              <Logo className="h-8 w-auto" />
              {!isCollapsed && <AppTitle className="text-xl font-bold text-navy-900 dark:text-navy-50" />} {/* Conditionally render AppTitle */}
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="lg:hidden rounded-md p-1 text-navy-600 hover:text-navy-900 dark:text-navy-300 dark:hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <nav className="flex-1 space-y-1 px-2 py-4">
            <SidebarLink
              to="/"
              icon={Home}
              label="Início"
              isActive={location.pathname === '/'}
              isCollapsed={isCollapsed}
            />

            {/* Parecer Automatizado Group */}
            <div>
              <button
                onClick={() => setIsParecerOpen(!isParecerOpen)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 transition-all duration-200 ${location.pathname === '/pareceres' || location.pathname === '/new'
                  ? 'bg-accent-400/10 text-accent-500'
                  : 'text-navy-600 hover:bg-navy-100 hover:text-accent-500 dark:text-navy-300 dark:hover:bg-navy-800'
                  }`}
              >
                <div className="flex items-center space-x-3">
                  <FileSearch className={`h-5 w-5 ${location.pathname === '/pareceres' || location.pathname === '/new' ? 'text-accent-500' : ''}`} />
                  {!isCollapsed && <span className="font-medium">Parecer automatizado</span>}
                </div>
                {!isCollapsed && (isParecerOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
              </button>

              {isParecerOpen && !isCollapsed && (
                <div className="mt-1 ml-4 space-y-1 border-l border-navy-200 dark:border-navy-700">
                  <SidebarLink
                    to="/pareceres"
                    icon={History}
                    label="Histórico"
                    isActive={location.pathname === '/pareceres'}
                    className="ml-2"
                  />
                  <SidebarLink
                    to="/new"
                    icon={PlusCircle}
                    label="Novo Parecer"
                    isActive={location.pathname === '/new'}
                    className="ml-2"
                  />
                </div>
              )}
            </div>

            <SidebarLink
              to="/consulta-especializada"
              icon={BookOpen}
              label="Consulta especializada"
              isActive={location.pathname === '/consulta-especializada'}
              isCollapsed={isCollapsed}
            />

            <SidebarLink
              to="/atos-administrativos"
              icon={ScrollText}
              label="Modelos de Atos Administrativos"
              isActive={location.pathname === '/atos-administrativos'}
              isCollapsed={isCollapsed}
            />

            <SidebarLink
              to="/chat"
              icon={MessageSquare}
              label="Hub de IAs"
              isActive={location.pathname === '/chat'}
              isCollapsed={isCollapsed}
            />

            <SidebarLink
              to="/settings"
              icon={Settings}
              label="Configurações"
              isActive={location.pathname === '/settings'}
              isCollapsed={isCollapsed}
            />
          </nav>

          <div className="border-t border-navy-200 dark:border-navy-700 p-4 space-y-2">
            <ThemeToggle
              isDarkMode={isDarkMode}
              onToggle={toggleDarkMode}
              isCollapsed={isCollapsed} // Pass isCollapsed prop
            />

            <button
              onClick={handleLogout}
              className="flex w-full items-center justify-center space-x-2 rounded-md px-3 py-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <LogOut className="h-5 w-5" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
